export type SavedRowCategoryOption = { value: string; label: string };

/**
 * Phase-1 default category handles (matches outreach-backend `SAVED_EMAIL_ROW_CATEGORY_HANDLES`).
 * Always surfaced in the save dialog and Bee Rows config so tenants see a primary tab before any saves.
 */
export const DEFAULT_SAVED_ROW_CATEGORY_HANDLES = ['user-rows'] as const;

/**
 * Optional curated labels for category slugs (Rows tab / dialog). Empty by default so categories come from the API
 * only; labels fall back to a readable form of the slug (`my-category` → `my category`).
 */
export const SAVED_EMAIL_ROW_CATEGORY_OPTIONS: readonly SavedRowCategoryOption[] = [];

export const MAX_SAVED_ROW_CATEGORY_SLUG_LENGTH = 64;

/** Lowercase slug: letters, digits, segments separated by a single hyphen and/or underscore (matches backend). */
const CATEGORY_SLUG_RE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

export const normalizeSavedRowCategoryInput = (raw: string): string =>
    raw
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/_+/g, '_')
        .replace(/^[-_]+|[-_]+$/g, '');

export const isValidSavedRowCategorySlug = (value: string): boolean =>
    value.length > 0 && value.length <= MAX_SAVED_ROW_CATEGORY_SLUG_LENGTH && CATEGORY_SLUG_RE.test(value);

/** Label for a category handle (known options first, else hyphen/underscore → space). */
export const labelForSavedRowCategoryHandle = (handle: string): string => {
    const known = SAVED_EMAIL_ROW_CATEGORY_OPTIONS.find((o) => o.value === handle);
    return known?.label ?? handle.replace(/-/g, ' ').replace(/_/g, ' ');
};

/** Autocomplete display string for a stored category slug (API option label, else friendly handle). */
export const categorySlugToDisplayInput = (slug: string, options: SavedRowCategoryOption[]): string => {
    const opt = options.find((o) => o.value === slug);
    return opt?.label ?? labelForSavedRowCategoryHandle(slug);
};

export type SavedRowLibraryLink = { savedRowId: string; name: string; category: string };

export type TemplateSavedRowLinkDto = { rowGuid: string; savedRowId: string; name: string; category: string };

const MAX_METADATA_WALK_DEPTH = 20;

/**
 * Bee instance id for template_rows.row_guid: usually `row.uuid`, but saved `bee_json` often only keeps it under
 * `metadata.uuid` (see Beefree nested row snapshots).
 */
export const getBeeRowInstanceUuid = (row: unknown): string => {
    if (!row || typeof row !== 'object') {
        return '';
    }
    const r = row as Record<string, unknown>;
    if (typeof r.uuid === 'string' && r.uuid.trim()) {
        return r.uuid.trim();
    }
    const meta = r.metadata;
    if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
        const u = (meta as Record<string, unknown>).uuid;
        if (typeof u === 'string' && u.trim()) {
            return u.trim();
        }
    }
    return '';
};

const isBeeRowWithContainer = (o: Record<string, unknown>): boolean =>
    o.container != null && typeof o.container === 'object' && !Array.isArray(o.container);

/** DFS: nested `metadata` blobs (saved bee_json) can carry `savedRowId` / `id` deeper than one level. */
const findLibraryMetaDeep = (node: unknown, depth: number): SavedRowLibraryLink | null => {
    if (depth > MAX_METADATA_WALK_DEPTH || node == null) {
        return null;
    }
    if (typeof node !== 'object') {
        return null;
    }
    if (Array.isArray(node)) {
        for (const item of node) {
            const hit = findLibraryMetaDeep(item, depth + 1);
            if (hit) {
                return hit;
            }
        }
        return null;
    }
    const o = node as Record<string, unknown>;
    const idRaw = o.savedRowId ?? o.id;
    if (typeof idRaw === 'string' && idRaw.trim()) {
        const nameFromMeta = typeof o.name === 'string' ? o.name.trim() : '';
        const category =
            (typeof o.category === 'string' && o.category.trim()) ||
            (typeof o.categoryHandle === 'string' && o.categoryHandle.trim()) ||
            '';
        if (nameFromMeta && category) {
            return { savedRowId: idRaw.trim(), name: nameFromMeta, category };
        }
    }
    for (const v of Object.values(o)) {
        const hit = findLibraryMetaDeep(v, depth + 1);
        if (hit) {
            return hit;
        }
    }
    return null;
};

/**
 * Deep-merge `template_rows` linkage into Bee JSON before the editor loads so Save → Update survives reload
 * (saved `bee_json` often omits `metadata`; `row_guid` still matches Bee’s row `uuid` when the template is saved).
 */
export const mergeTemplateSavedRowLinksIntoBeeJson = (
    beeJson: unknown,
    links: TemplateSavedRowLinkDto[] | undefined | null
): unknown => {
    if (!links?.length || beeJson == null || typeof beeJson !== 'object') {
        return beeJson;
    }
    const byGuid = new Map(links.map((l) => [l.rowGuid, l]));
    const clone = JSON.parse(JSON.stringify(beeJson)) as unknown;

    const visit = (node: unknown): void => {
        if (!node || typeof node !== 'object') {
            return;
        }
        if (Array.isArray(node)) {
            for (const item of node) {
                visit(item);
            }
            return;
        }
        const o = node as Record<string, unknown>;
        if (isBeeRowWithContainer(o)) {
            const uuid = getBeeRowInstanceUuid(o);
            const link = uuid ? byGuid.get(uuid) : undefined;
            if (link) {
                const existingMeta =
                    o.metadata && typeof o.metadata === 'object' && !Array.isArray(o.metadata)
                        ? { ...(o.metadata as Record<string, unknown>) }
                        : {};
                o.metadata = {
                    ...existingMeta,
                    id: link.savedRowId,
                    savedRowId: link.savedRowId,
                    name: link.name,
                    category: link.category,
                    categoryHandle: link.category,
                };
                if (typeof o.uuid !== 'string' || !o.uuid.trim()) {
                    o.uuid = uuid;
                }
                const rowName = typeof o.name === 'string' ? o.name.trim() : '';
                if (!rowName) {
                    o.name = link.name;
                }
            }
        }
        for (const v of Object.values(o)) {
            visit(v);
        }
    };

    visit(clone);
    return clone;
};

/**
 * Merge GET /saved-rows/template-links/:templateId with optional in-session fallback (`templateId` null → canvas only).
 * When a template id exists, API rows win; canvas fills gaps until the link query refetches.
 */
export const buildSavedRowLinkLookupMap = (
    apiLinks: TemplateSavedRowLinkDto[] | undefined,
    templateId: number | null,
    canvasFallback: ReadonlyMap<string, SavedRowLibraryLink>
): Map<string, SavedRowLibraryLink> => {
    const m = new Map<string, SavedRowLibraryLink>();
    if (templateId == null) {
        canvasFallback.forEach((v, k) => m.set(k, v));
    } else {
        for (const l of apiLinks ?? []) {
            m.set(l.rowGuid, { savedRowId: l.savedRowId, name: l.name, category: l.category });
        }
        canvasFallback.forEach((v, k) => {
            if (!m.has(k)) {
                m.set(k, v);
            }
        });
    }
    return m;
};

/** Reads library linkage from a Bee row (from GET /saved-rows/categories/:handle via `mapRowForGetRows`). */
export const getSavedRowLibraryMetadataFromPluginRow = (
    row: unknown
): { savedRowId: string; name: string; category: string } | null => {
    if (!row || typeof row !== 'object') {
        return null;
    }
    const r = row as Record<string, unknown>;
    const meta = r.metadata;
    if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
        const m = meta as Record<string, unknown>;
        const idRaw = m.savedRowId ?? m.id;
        if (typeof idRaw === 'string' && idRaw.trim()) {
            const nameFromMeta = typeof m.name === 'string' ? m.name.trim() : '';
            const name = nameFromMeta || (typeof r.name === 'string' ? r.name.trim() : '');
            const category =
                (typeof m.category === 'string' && m.category.trim()) ||
                (typeof m.categoryHandle === 'string' && m.categoryHandle.trim()) ||
                '';
            if (name && category) {
                return { savedRowId: idRaw.trim(), name, category };
            }
        }
        const deep = findLibraryMetaDeep(meta, 0);
        if (deep) {
            return deep;
        }
    }
    return findLibraryMetaDeep(row, 0);
};

/** Prefer row `metadata`; else `linkByRowUuid` (from `template_rows` + optional canvas fallback). */
export const resolveSavedRowLibraryLink = (
    row: unknown,
    linkByRowUuid: ReadonlyMap<string, SavedRowLibraryLink>
): SavedRowLibraryLink | null => {
    const fromMeta = getSavedRowLibraryMetadataFromPluginRow(row);
    if (fromMeta) {
        return fromMeta;
    }
    const uuid = getBeeRowInstanceUuid(row);
    if (!uuid) {
        return null;
    }
    return linkByRowUuid.get(uuid) ?? null;
};

/** Map free-typed or label text to a category slug (`options` only — no built-in rows in the picker). */
export const resolveSavedRowCategorySlugFromInput = (input: string, options: SavedRowCategoryOption[]): string => {
    const trimmed = input.trim();
    if (!trimmed) {
        return '';
    }
    const lookup: SavedRowCategoryOption[] = [...options];
    const byLabel = lookup.find((o) => o.label.toLowerCase() === trimmed.toLowerCase());
    if (byLabel) {
        return byLabel.value;
    }
    const byValue = lookup.find((o) => o.value === trimmed.toLowerCase());
    if (byValue) {
        return byValue.value;
    }
    return normalizeSavedRowCategoryInput(trimmed);
};
